/**
 * LaGift — share
 * ---------------------------------------------------------------------------
 * Upgrades a plain link to the product into the phone's own share sheet, or a
 * copy-to-clipboard on a desktop.
 *
 * The markup is an ordinary `<a href>` and stays one. With the script gone it
 * opens the product, which is a reasonable thing for a "share" link to do and
 * can be copied out of the address bar. Nothing here is required for the page
 * to work.
 *
 * `navigator.share` and `navigator.clipboard` are both unavailable on an
 * insecure origin and both can be refused by the user, so every path falls back
 * rather than assuming. A share the customer cancels is not an error and is not
 * reported as one.
 */
/**
 * Scoped so the file can run more than once. A `<script src>` executes once per
 * tag, not once per URL, and a top-level class throws "already been declared"
 * on the second run. See CLAUDE.md.
 */
(() => {
  class LagiftShare extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-share-trigger]');
      this.label = this.querySelector('[data-share-label]');
      this.originalLabel = this.label?.textContent ?? '';

      this.trigger?.addEventListener('click', (event) => this.onClick(event));
    }

    get url() {
      return this.dataset.shareUrl || window.location.href;
    }

    async onClick(event) {
      // Let a modified click do what the browser would: open in a new tab, save
      // the link, whatever the customer actually asked for.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      if (navigator.share) {
        event.preventDefault();
        try {
          await navigator.share({ title: this.dataset.shareTitle || document.title, url: this.url });
        } catch (error) {
          // AbortError is the customer closing the sheet. Nothing went wrong.
          if (error?.name !== 'AbortError') this.copy(event);
        }
        return;
      }

      if (navigator.clipboard?.writeText) {
        event.preventDefault();
        this.copy();
      }

      // No share, no clipboard: the anchor is left alone and opens the product.
    }

    async copy() {
      try {
        await navigator.clipboard.writeText(this.url);
        this.confirm();
      } catch (error) {
        // Refused or unavailable. The link still works; say nothing rather than
        // showing a failure for something the customer did not ask to fail.
      }
    }

    confirm() {
      if (!this.label) return;

      this.label.textContent = this.dataset.copied || this.originalLabel;
      this.classList.add('is-copied');

      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => {
        this.label.textContent = this.originalLabel;
        this.classList.remove('is-copied');
      }, 2000);
    }
  }

  if (!customElements.get('lagift-share')) {
    customElements.define('lagift-share', LagiftShare);
  }
})();
